/**
 * Build Your Door - customer information gate.
 *
 * Public visitors must complete a short Gravity Form before the DriveWorks
 * configurator is allowed to boot. The captured details are pushed into the CPQ
 * DriveApp so they reach the J&E sales team.
 *
 * Two separate things are stored, both in localStorage so they survive new tabs
 * and browser restarts:
 *
 *   dwCustomer          The quote in progress - contact details plus the
 *                       Gravity Forms entry id and its write token. The visitor
 *                       is not asked for anything while this exists. Cleared
 *                       when the quote is submitted, so the next order creates
 *                       a new Gravity Forms entry.
 *
 *   dwCustomerPersisted Contact details only, kept after a quote completes, so
 *                       the gate form comes back pre-filled next time. The
 *                       visitor still has to submit it, which is what creates
 *                       the new entry.
 *
 * Clear both with DWGate.reset() when testing.
 *
 * The internal J&E workflow lives entirely under /catalog/ and is never gated.
 * The public workflow lives under /build-your-door/ and always is. The two are
 * told apart by URL alone and nothing carries over between them - see
 * resolveFlow().
 *
 * Loaded on every DriveWorks page, including the ones that show no gate.
 *
 * Running a DriveWorks Specification costs the client money, so on a gated page
 * PHP does not enqueue the boot script (product.js / catalog.js / Basket.js) at
 * all - this file injects it, and only once the gate has been satisfied. That
 * means the gate fails closed: if this script is missing or throws, DriveWorks
 * is never started.
 *
 * Exposes window.DWGate:
 *   getCustomer()    Captured customer object, or null.
 *   getFlow()        "internal" or "public".
 *   reportOrderId()  Saves a DriveWorks order id onto the Gravity Forms entry.
 *   clearSession()   Drops the quote in progress, keeps the pre-fill details.
 *   reset()          Testing only - forgets the visitor entirely. Gate data
 *                    only; never touches the DriveWorks order or basket.
 */

const DW_GATE_FLOW_KEY = "dwFlow";
const DW_GATE_CUSTOMER_KEY = "dwCustomer";
const DW_GATE_REPORTED_KEY = "dwReportedOrderId";

/**
 * How long a quote in progress stays linked to its Gravity Forms entry.
 *
 * Long enough that a visitor can leave a configuration and come back to it days
 * later, short enough that an abandoned quote does not keep writing to a stale
 * entry months afterwards. Shorter than DW_GATE_PERSIST_DAYS on purpose - when
 * this runs out the visitor is asked again, but the form arrives pre-filled.
 */
const DW_GATE_CUSTOMER_DAYS = 30;

/**
 * Remember the contact details after a quote completes, so the gate form is
 * pre-filled the next time the same visitor starts an order. The form is still
 * submitted every time, so every quote gets its own Gravity Forms entry.
 */
const DW_GATE_PERSIST_CUSTOMER = true;
const DW_GATE_PERSIST_KEY = "dwCustomerPersisted";
const DW_GATE_PERSIST_DAYS = 90;

const DWGate = (function () {
    const settings = window.DriveworksGate || {};

    let overlay = null;
    let dialog = null;
    let flow = "public";
    let isOpen = false;
    let hasBooted = false;

    /**
     * Resolves when DriveWorks is allowed to start.
     *
     * Only used by the deprecated ready() shim below - the gate itself works by
     * withholding the boot script, not by waiting on this.
     */
    let permitBootResolve = null;
    const bootPermission = new Promise(resolve => {
        permitBootResolve = resolve;
    });

    /**
     * Allow DriveWorks to start, and start it if we are the one holding the
     * script back.
     */
    function permitBoot() {
        if (permitBootResolve) {
            permitBootResolve();
            permitBootResolve = null;
        }

        bootConfigurator();
    }

    function logDecision(message) {
        console.log("%cDW GATE", "color: royalblue;", message);
    }

    /**
     * Start DriveWorks by injecting the boot script PHP deliberately withheld.
     *
     * This is the ONLY place the configurator can be started on a gated page.
     * bootScript is empty on pages that are not gated - there PHP enqueued the
     * script normally and this does nothing.
     */
    function bootConfigurator() {
        if (hasBooted || !settings.bootScript) {
            return;
        }

        hasBooted = true;

        const script = document.createElement("script");
        script.src = settings.bootScript;
        document.body.appendChild(script);

        logDecision("starting DriveWorks");
    }

    /* -----------------------------------------------------------------------
     * Storage helpers - every access is guarded, storage can throw in private
     * browsing modes and when a browser is set to block site data.
     * -------------------------------------------------------------------- */

    function readStore(store, key) {
        try {
            return window[store].getItem(key);
        } catch (error) {
            return null;
        }
    }

    function writeStore(store, key, value) {
        try {
            window[store].setItem(key, value);
        } catch (error) {
            /* Storage unavailable - the gate simply shows again next page. */
        }
    }

    function removeStore(store, key) {
        try {
            window[store].removeItem(key);
        } catch (error) {
            /* Nothing to do. */
        }
    }

    /* -----------------------------------------------------------------------
     * Flow detection
     *
     * The workflow is decided by the current URL alone - PHP sends "internal"
     * for /catalog/ and everything under it, "public" for everything else.
     *
     * Nothing is remembered between pages on purpose. The two workflows do not
     * overlap: internal users stay inside /catalog/ URLs for their whole
     * journey, so the same person visiting a /build-your-door/ page is treated
     * as a normal visitor and is asked for their details.
     * -------------------------------------------------------------------- */

    function resolveFlow() {
        // Clean up the flags written by earlier versions of this file, which
        // carried the internal exemption across pages. Can be removed once they
        // have had time to disappear from visitors' browsers.
        removeStore("localStorage", DW_GATE_FLOW_KEY);
        removeStore("sessionStorage", DW_GATE_FLOW_KEY);

        return settings.flow === "internal" ? "internal" : "public";
    }

    /* -----------------------------------------------------------------------
     * Expiring records
     *
     * Both stores age out, so nothing the gate writes lives in a visitor's
     * browser forever. Same shape as the one used by
     * partials/campaign-ids-for-gravity-forms.php.
     * -------------------------------------------------------------------- */

    function writeRecord(key, value, days) {
        writeStore("localStorage", key, JSON.stringify({
            value: value,
            expiryDate: new Date().getTime() + days * 24 * 60 * 60 * 1000,
        }));
    }

    function readRecord(key) {
        const raw = readStore("localStorage", key);

        if (!raw) {
            return null;
        }

        try {
            const record = JSON.parse(raw);

            if (!record) {
                return null;
            }

            if (record.expiryDate && new Date().getTime() > record.expiryDate) {
                removeStore("localStorage", key);

                return null;
            }

            // Records written before expiry dates were added are not wrapped.
            return record.value !== undefined ? record.value : record;
        } catch (error) {
            return null;
        }
    }

    /* -----------------------------------------------------------------------
     * Captured customer
     * -------------------------------------------------------------------- */

    function getCustomer() {
        // Internal users never pass the gate and never send customer data.
        if (flow === "internal") {
            return null;
        }

        const customer = readRecord(DW_GATE_CUSTOMER_KEY);

        return customer && customer.entryId ? customer : null;
    }

    function storeCustomer(customer) {
        writeRecord(DW_GATE_CUSTOMER_KEY, customer, DW_GATE_CUSTOMER_DAYS);
        persistCustomer(customer);

        console.log("%cGATE PASSED", "color: royalblue;", "Entry", customer.entryId);
    }

    function clearSession() {
        removeStore("localStorage", DW_GATE_CUSTOMER_KEY);
        removeStore("localStorage", DW_GATE_REPORTED_KEY);
    }

    /**
     * Testing helper - forget the visitor, so the gate form is requested again
     * from scratch with empty fields.
     *
     * Only touches keys this gate owns. DriveWorks state - the order, the
     * basket contents, the session - is deliberately left exactly as it is, so
     * resetting the gate never disturbs a basket you are in the middle of
     * testing.
     *
     * Run DWGate.reset() in the console, then reload.
     */
    function reset() {
        const cleared = [
            DW_GATE_CUSTOMER_KEY,
            DW_GATE_PERSIST_KEY,
            DW_GATE_REPORTED_KEY,
            DW_GATE_FLOW_KEY,
        ];

        cleared.forEach(key => {
            removeStore("localStorage", key);
            removeStore("sessionStorage", key);
        });

        console.log("%cDW GATE", "color: royalblue;", "gate data cleared, DriveWorks basket untouched - reload the page");

        return cleared;
    }

    /* -----------------------------------------------------------------------
     * Long term customer storage
     *
     * Contact details only - never the entry id or its write token, so a
     * finished quote can never be written to by a later session. Survives
     * clearSession() on purpose: that is what makes the next gate form arrive
     * pre-filled. Uses the expiry pattern from
     * partials/campaign-ids-for-gravity-forms.php.
     * -------------------------------------------------------------------- */

    function persistCustomer(customer) {
        if (!DW_GATE_PERSIST_CUSTOMER) {
            return;
        }

        // Contact details only - deliberately no entryId and no token.
        writeRecord(DW_GATE_PERSIST_KEY, {
            firstName: customer.firstName,
            lastName: customer.lastName,
            phone: customer.phone,
            email: customer.email,
            customerType: customer.customerType,
            companyName: customer.companyName,
        }, DW_GATE_PERSIST_DAYS);
    }

    function readPersistedCustomer() {
        if (!DW_GATE_PERSIST_CUSTOMER) {
            return null;
        }

        return readRecord(DW_GATE_PERSIST_KEY);
    }

    function prefillGateForm() {
        const saved = readPersistedCustomer();
        const fields = settings.fields || {};

        if (!saved || !settings.formId) {
            return;
        }

        Object.keys(fields).forEach(key => {
            if (!saved[key]) {
                return;
            }

            // Radio fields (Customer Type) have one input per choice.
            const choices = document.querySelectorAll("#gform_" + settings.formId + " input[type=radio][name='input_" + fields[key] + "']");

            if (choices.length) {
                prefillChoice(choices, saved[key]);

                return;
            }

            const input = document.getElementById("input_" + settings.formId + "_" + fields[key]);

            if (input && !input.value) {
                input.value = saved[key];
            }
        });
    }

    function prefillChoice(choices, value) {
        const isChosen = Array.prototype.some.call(choices, choice => choice.checked);
        const match = Array.prototype.find.call(choices, choice => choice.value === value);

        if (isChosen || !match) {
            return;
        }

        // A real click, so Gravity Forms conditional logic runs and shows
        // Company Name for contractors.
        match.click();
    }

    /* -----------------------------------------------------------------------
     * Overlay
     * -------------------------------------------------------------------- */

    function openGate() {
        if (!overlay || isOpen) {
            return;
        }

        isOpen = true;
        overlay.hidden = false;
        document.body.classList.add("dw-gate-open");

        const page = document.getElementById("page");

        if (page) {
            page.setAttribute("aria-hidden", "true");
            page.inert = true;
        }

        document.addEventListener("keydown", handleKeydown, true);
        focusFirstElement();
    }

    function closeGate() {
        if (!overlay || !isOpen) {
            return;
        }

        isOpen = false;
        overlay.hidden = true;
        document.body.classList.remove("dw-gate-open");

        const page = document.getElementById("page");

        if (page) {
            page.removeAttribute("aria-hidden");
            page.inert = false;
        }

        document.removeEventListener("keydown", handleKeydown, true);
    }

    function getFocusable() {
        if (!dialog) {
            return [];
        }

        const selector = [
            "a[href]",
            "button:not([disabled])",
            "input:not([type=hidden]):not([disabled])",
            "select:not([disabled])",
            "textarea:not([disabled])",
            "[tabindex]:not([tabindex='-1'])",
        ].join(", ");

        return Array.prototype.filter.call(dialog.querySelectorAll(selector), element => {
            return element.offsetWidth > 0 || element.offsetHeight > 0 || element === document.activeElement;
        });
    }

    function focusFirstElement() {
        const focusable = getFocusable();

        if (focusable.length) {
            focusable[0].focus();

            return;
        }

        if (dialog) {
            dialog.setAttribute("tabindex", "-1");
            dialog.focus();
        }
    }

    /**
     * The gate is deliberately not dismissible - there is no Escape handler and
     * no click-outside handler. This only keeps focus inside the dialog.
     */
    function handleKeydown(event) {
        if (event.key !== "Tab") {
            return;
        }

        const focusable = getFocusable();

        if (!focusable.length) {
            return;
        }

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();

            return;
        }

        if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    }

    /**
     * Back out of the gate: return to the previous page when it belongs to this
     * site, otherwise fall back to the products page.
     */
    function goBack() {
        const referrer = document.referrer;
        let isLocalReferrer = false;

        try {
            isLocalReferrer = Boolean(referrer) && new URL(referrer).origin === window.location.origin;
        } catch (error) {
            isLocalReferrer = false;
        }

        if (isLocalReferrer && window.history.length > 1) {
            window.history.back();

            return;
        }

        window.location.href = settings.productsUrl || "/";
    }

    /* -----------------------------------------------------------------------
     * Reading the submitted entry
     *
     * The gform_confirmation filter wraps the confirmation message in an
     * element carrying the entry id and the submitted values, so we can read
     * them whether the form was submitted over AJAX or with a page reload.
     * -------------------------------------------------------------------- */

    function captureSubmission() {
        const success = document.querySelector("[data-dw-gate-success]");

        if (!success) {
            return false;
        }

        let customer = null;

        try {
            customer = JSON.parse(success.getAttribute("data-dw-gate-customer") || "null");
        } catch (error) {
            customer = null;
        }

        if (!customer || !customer.entryId) {
            return false;
        }

        storeCustomer(customer);
        closeGate();
        permitBoot();

        return true;
    }

    function watchForSubmission() {
        if (window.jQuery) {
            jQuery(document).on("gform_confirmation_loaded", () => captureSubmission());
            jQuery(document).on("gform_post_render", () => prefillGateForm());
        }

        // Fallback for any Gravity Forms version that does not fire the jQuery
        // events, and for confirmations rendered without AJAX.
        if (overlay && window.MutationObserver) {
            const observer = new MutationObserver(() => {
                if (captureSubmission()) {
                    observer.disconnect();
                }
            });

            observer.observe(overlay, { childList: true, subtree: true });
        }
    }

    /* -----------------------------------------------------------------------
     * Sending the DriveWorks order id back to the Gravity Forms entry
     * -------------------------------------------------------------------- */

    /**
     * @param orderId   DriveWorks ParameterOrderId - a GUID, used for matching
     *                  by machine.
     * @param orderName The readable reference the sales team sees, e.g.
     *                  "DW-431840". This is what goes in the visible field.
     */
    function reportOrderId(orderId, orderName) {
        const customer = getCustomer();

        if (!orderId) {
            logDecision("order id not saved - DriveWorks has not assigned an order yet");
            return;
        }

        if (!customer || !customer.token) {
            logDecision("order id not saved - no gate entry for this visitor");
            return;
        }

        if (!settings.ajaxUrl || !settings.nonce) {
            logDecision("order id not saved - AJAX settings missing from the page");
            return;
        }

        const reference = String(orderName || orderId);

        // Scoped to the entry: a new entry must receive the reference even when
        // the DriveWorks order is the same one reported for a previous entry.
        const reportedKey = customer.entryId + "|" + reference;

        if (readStore("localStorage", DW_GATE_REPORTED_KEY) === reportedKey) {
            logDecision("order id already saved on entry " + customer.entryId + ": " + reference);
            return;
        }

        const body = new URLSearchParams();

        body.append("action", "dw_gate_order_id");
        body.append("nonce", settings.nonce);
        body.append("entry_id", customer.entryId);
        body.append("token", customer.token);
        body.append("order_id", orderId);
        body.append("order_name", orderName || "");

        // Marked before sending so two events in quick succession do not both
        // write, and cleared again below if the save does not succeed.
        writeStore("localStorage", DW_GATE_REPORTED_KEY, reportedKey);

        const forget = reason => {
            removeStore("localStorage", DW_GATE_REPORTED_KEY);
            logDecision("order id NOT saved on entry " + customer.entryId + " - " + reason);
        };

        try {
            // keepalive: Basket.js redirects to the confirmation page right
            // after calling this, and without it the browser would cancel the
            // request mid-flight. The logging below will not be visible once
            // the page has navigated, but the save still completes.
            fetch(settings.ajaxUrl, {
                method: "POST",
                body: body,
                credentials: "same-origin",
                keepalive: true,
            })
                .then(response => response.text().then(text => ({ status: response.status, text: text })))
                .then(({ status, text }) => {
                    let json = null;

                    try {
                        json = JSON.parse(text);
                    } catch (error) {
                        json = null;
                    }

                    if (json && json.success) {
                        logDecision("order id saved on entry " + customer.entryId + ": " + reference);
                        return;
                    }

                    // "-1" with 403 is WordPress rejecting the nonce, typically a
                    // cached page carrying an expired one.
                    const message = json && json.data && json.data.message ? json.data.message : text;
                    forget("HTTP " + status + ": " + message);
                })
                .catch(error => forget("request failed: " + error));
        } catch (error) {
            forget("request could not be sent: " + error);
        }
    }

    /* -----------------------------------------------------------------------
     * Init
     * -------------------------------------------------------------------- */

    function init() {
        overlay = document.querySelector("[data-dw-gate]");
        dialog = document.querySelector("[data-dw-gate-dialog]");
        flow = resolveFlow();

        const backButton = document.querySelector("[data-dw-gate-back]");

        if (backButton) {
            backButton.addEventListener("click", goBack);
        }

        // The quote has been submitted - start the next visit fresh. Basket.js
        // already clears this on QuoteUpdated, this only covers the case where
        // DriveWorks reaches the confirmation page some other way.
        if (settings.isConfirmation) {
            clearSession();
        }

        // Not a gated page - PHP enqueued the DriveWorks scripts itself.
        if (!settings.gate) {
            permitBoot();
            return;
        }

        // Internal J&E user who came in through /catalog/.
        if (flow === "internal") {
            logDecision("internal catalog user, no form required");
            permitBoot();

            return;
        }

        // An empty basket has nothing to quote, so there is no point asking for
        // contact details yet.
        if (settings.gate === "basket" && !readStore("localStorage", "orderId")) {
            logDecision("empty basket, no form required");
            permitBoot();

            return;
        }

        // Already completed the form earlier in this browser session.
        if (getCustomer()) {
            logDecision("form already completed this session");
            permitBoot();

            return;
        }

        // Fail closed. Without the form we must not start DriveWorks, because
        // starting it is what costs the client money.
        if (!overlay) {
            logDecision("gate form is missing - DriveWorks NOT started");

            return;
        }

        watchForSubmission();

        // A non-AJAX submission renders the confirmation on page load.
        if (captureSubmission()) {
            return;
        }

        prefillGateForm();
        openGate();
        logDecision("waiting for the customer form - DriveWorks not started");
    }

    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", init);
    } else {
        init();
    }

    return {
        /**
         * DEPRECATED compatibility shim.
         *
         * An earlier version of the gate had product.js / catalog.js /
         * Basket.js wait on this promise. Those files now simply are not on the
         * page until the gate allows it, so nothing in the theme calls this any
         * more - but a stale cached or partially deployed copy of them still
         * might, and without this the whole page would die on a TypeError.
         *
         * It resolves only when DriveWorks is actually allowed to start, so an
         * old file cannot use it to bypass the gate. Safe to delete once every
         * environment is running the current JavaScript.
         */
        ready: () => bootPermission,
        getCustomer: getCustomer,
        getFlow: () => flow,
        reportOrderId: reportOrderId,
        clearSession: clearSession,
        reset: reset,
    };
})();

window.DWGate = DWGate;
